import { Component, effect, EventEmitter, Input, OnChanges, OnInit, Output, signal, SimpleChanges, ViewChild, ViewEncapsulation, AfterViewInit, NgZone, ElementRef, inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
// import timeGridPlugin from '@fullcalendar/timegrid';
import rrulePlugin from '@fullcalendar/rrule';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';

@Component({
  selector: 'black-event-calendar',
  templateUrl: './event-calendar.component.html',
  styleUrl: './event-calendar.component.css',
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false,
})
export class EventCalendarComponent
  implements OnChanges, AfterViewInit, OnInit, OnDestroy
{
  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);

  @Input()
  events: EventInput[] = [];

  @ViewChild('calendar') calendarComponent: FullCalendarComponent | undefined;
  @ViewChild('calendar', { read: ElementRef }) calendarElementRef:
    | ElementRef
    | undefined;

  screenSize = signal('none');
  isCalendarReady = signal(false);

  calendarOptions: CalendarOptions = {
    initialView: 'listWeek',
    plugins: [dayGridPlugin, listPlugin, rrulePlugin, interactionPlugin],
    dayMaxEventRows: true, // for all non-TimeGrid views
    views: {
      timeGrid: {
        dayMaxEventRows: 6, // adjust to 6 only for timeGridWeek/timeGridDay
      },
    },
    headerToolbar: {
      start: 'title',
      center: '',
      end: 'prev,next',
    },
    dateClick: (event) => this.handleDateClick(event),
    eventClick: (event) => this.handleEventClick(event),
    selectable: true,
    eventDisplay: 'list-item',
  };

  private resizeObserver: ResizeObserver;
  private mutationObserver: MutationObserver;
  private mediaQuerySmall!: MediaQueryList;
  private mediaQueryMedium!: MediaQueryList;
  private mediaQuerySmallListener!: (e: MediaQueryListEvent) => void;
  private mediaQueryMediumListener!: (e: MediaQueryListEvent) => void;

  @Output()
  dateSelect = new EventEmitter<Date>();

  @Output()
  eventClick = new EventEmitter<{
    shareLinkId: string;
    isAccessible?: boolean;
    isPending?: boolean;
    liveClassId?: string;
    meetingName?: string;
    liveClassName?: string;
    targetEventDate?: Date;
  }>();

  handleDateClick(event: DateClickArg) {
    this.dateSelect.emit(event.date);
  }

  handleEventClick(event: any) {
    const extendedProps = event.event.extendedProps || {};
    const shareLinkId = extendedProps.shareLinkId;
    if (
      shareLinkId ||
      extendedProps.isAccessible === false ||
      extendedProps.isPending
    ) {
      this.eventClick.emit({
        shareLinkId,
        isAccessible:
          extendedProps.isAccessible !== false && !extendedProps.isPending,
        isPending: Boolean(extendedProps.isPending),
        liveClassId: extendedProps.liveClassId,
        meetingName: extendedProps.meetingName,
        liveClassName: extendedProps.liveClassName,
        targetEventDate: extendedProps.targetEventDate,
      });
    }
  }

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.mediaQuerySmall = window.matchMedia('(max-width: 959px)');
      this.mediaQueryMedium = window.matchMedia('(min-width: 960px) and (max-width: 1279px)');

      this.mediaQuerySmallListener = (e: MediaQueryListEvent) => {
        this.ngZone.run(() => {
          if (e.matches) { this.screenSize.set('small'); }
          else if (this.mediaQueryMedium.matches) { this.screenSize.set('medium'); }
          this.updateCalendarView();
        });
      };
      this.mediaQueryMediumListener = (e: MediaQueryListEvent) => {
        this.ngZone.run(() => {
          if (this.mediaQuerySmall.matches) { this.screenSize.set('small'); }
          else if (e.matches) { this.screenSize.set('medium'); }
          this.updateCalendarView();
        });
      };

      this.mediaQuerySmall.addEventListener('change', this.mediaQuerySmallListener);
      this.mediaQueryMedium.addEventListener('change', this.mediaQueryMediumListener);

      // Set initial screen size
      if (this.mediaQuerySmall.matches) { this.screenSize.set('small'); }
      else if (this.mediaQueryMedium.matches) { this.screenSize.set('medium'); }
    }

    effect(() => {
      if (this.isCalendarReady()) {
        this.updateCalendarView();
      }
    });

    // Initialize observers
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        this.handleResize();
      });
    });

    this.mutationObserver = new MutationObserver((mutations) => {
      this.ngZone.run(() => {
        const visibilityChanged = mutations.some(
          (mutation) =>
            mutation.type === 'attributes' &&
            (mutation.attributeName === 'style' ||
              mutation.attributeName === 'class')
        );
        if (visibilityChanged) {
          this.handleVisibilityChange();
        }
      });
    });
  }

  ngOnInit() {
    // Start observing the parent tab container for visibility changes
    const parentElement = this.findParentTab();
    if (parentElement) {
      this.mutationObserver.observe(parentElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }
  }

  private findParentTab(): HTMLElement | null {
    let element = document.querySelector('black-event-calendar')?.parentElement;
    while (element) {
      if (element.hasAttribute('blackTabsContent')) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  private handleResize() {
    if (this.calendarComponent?.getApi()) {
      this.calendarComponent.getApi().updateSize();
    }
  }

  private handleVisibilityChange() {
    setTimeout(() => {
      if (this.calendarComponent?.getApi()) {
        this.calendarComponent.getApi().updateSize();
        if (this.events.length > 0) {
          this.calendarComponent.getApi().render();
        }
      }
    }, 0);
  }

  private updateCalendarView() {
    if (this.calendarComponent?.getApi()) {
      if (this.screenSize() === 'small') {
        this.calendarComponent.getApi()?.changeView('listWeek');
      } else {
        this.calendarComponent.getApi()?.changeView('dayGridMonth');
      }
      this.calendarComponent.getApi().updateSize();
    }
  }

  ngAfterViewInit() {
    // Wait for the calendar to be fully initialized
    if (this.calendarComponent?.getApi()) {
      this.isCalendarReady.set(true);
      this.updateCalendarView();
      if (this.events.length > 0) {
        this.calendarOptions.events = this.events;
        this.calendarComponent.getApi().render();
      }

      // Start observing the calendar container for size changes
      if (this.calendarElementRef) {
        this.resizeObserver.observe(this.calendarElementRef.nativeElement);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['events'] && this.isCalendarReady()) {
      this.calendarOptions.events = this.events;
      this.calendarComponent?.getApi()?.render();
    }
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();
    if (isPlatformBrowser(this.platformId)) {
      this.mediaQuerySmall?.removeEventListener('change', this.mediaQuerySmallListener);
      this.mediaQueryMedium?.removeEventListener('change', this.mediaQueryMediumListener);
    }
  }
}
